import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { ArrowLeft, Send, Trophy, Target, Award, Check, ChevronsUpDown, Plus, Minus } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Searchable Player Dropdown Component
function PlayerSearchDropdown({ players, onSelect, placeholder, selectedValue, allowClear, onClear }) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const sortedPlayers = [...players].sort((a, b) => 
    a.child_name.localeCompare(b.child_name)
  );

  const selectedPlayer = selectedValue 
    ? players.find(p => p.id === selectedValue) 
    : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between bg-slate-900/50"
        >
          <span className={selectedPlayer ? "text-white" : "text-slate-400"}>
            {selectedPlayer ? selectedPlayer.child_name : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command>
          <CommandInput 
            placeholder="Type to search..." 
            value={searchValue}
            onValueChange={setSearchValue}
          />
          <CommandList>
            <CommandEmpty>No player found.</CommandEmpty>
            <CommandGroup>
              {allowClear && selectedValue && (
                <CommandItem
                  value="__clear__"
                  onSelect={() => {
                    if (onClear) onClear();
                    setOpen(false);
                    setSearchValue("");
                  }}
                  className="text-red-400 border-b"
                >
                  ✕ Clear Selection
                </CommandItem>
              )}
              {sortedPlayers
                .filter(player => 
                  player.child_name.toLowerCase().includes(searchValue.toLowerCase())
                )
                .map((player) => (
                  <CommandItem
                    key={player.id}
                    value={player.child_name}
                    onSelect={() => {
                      onSelect(player.id);
                      setOpen(false);
                      setSearchValue("");
                    }}
                  >
                    <Check
                      className={`mr-2 h-4 w-4 ${
                        selectedValue === player.id ? "opacity-100" : "opacity-0"
                      }`}
                    />
                    <div>
                      <p className="font-medium">{player.child_name}</p>
                      <p className="text-xs text-slate-400">{player.membership_number}</p>
                    </div>
                  </CommandItem>
                ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function AcademyMatchReport({ user }) {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  
  const [session, setSession] = useState(null);
  const [players, setPlayers] = useState([]);
  const [reportType, setReportType] = useState("match");
  const [result, setResult] = useState("");
  const [ourScore, setOurScore] = useState("");
  const [opponentScore, setOpponentScore] = useState("");
  const [opponentName, setOpponentName] = useState("");
  const [matchNotes, setMatchNotes] = useState("");
  const [playerStats, setPlayerStats] = useState({});
  const [goals, setGoals] = useState([]); // Array of {scorer: playerId|'OG', assist: playerId|null}
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [existingReport, setExistingReport] = useState(null);

  const isCoach = user?.roles?.includes("academy_coach");
  const isAdmin = ["convenor", "captain"].includes(user?.role);

  useEffect(() => {
    fetchSessionAndPlayers();
  }, [sessionId]);

  const fetchSessionAndPlayers = async () => {
    try {
      const sessionsResponse = await axios.get(`${API}/academy/sessions`, {
        withCredentials: true
      });
      const foundSession = sessionsResponse.data.find(s => s.id === sessionId);
      setSession(foundSession);

      if (foundSession) {
        // Check if report already exists (edit mode)
        try {
          const reportResponse = await axios.get(`${API}/academy/sessions/${sessionId}/match-report`, {
            withCredentials: true
          });
          setIsEditMode(true);
          setExistingReport(reportResponse.data);
          
          // Pre-fill form with existing data
          setReportType(reportResponse.data.type || "match");
          setResult(reportResponse.data.result || "");
          setOurScore(reportResponse.data.our_score || "");
          setOpponentScore(reportResponse.data.opponent_score || "");
          setOpponentName(reportResponse.data.opponent_name || "");
          setMatchNotes(reportResponse.data.match_notes || "");
        } catch (err) {
          // No existing report, continue with new submission
        }
        
        const teamResponse = await axios.get(`${API}/academy/teams/${foundSession.team_id}`, {
          withCredentials: true
        });
        
        const initialStats = {};
        teamResponse.data.players.forEach(player => {
          // If editing, load existing player stats for MOTM
          const existingStat = isEditMode && existingReport?.player_stats?.find(s => s.player_id === player.id);
          initialStats[player.id] = {
            goals: 0,
            assists: 0,
            motm: existingStat?.motm || false
          };
        });
        setPlayerStats(initialStats);
        setPlayers(teamResponse.data.players);

        // If editing, reconstruct goals array from player stats
        // This is a best-effort reconstruction since we don't store individual goal events
        if (isEditMode && existingReport?.player_stats) {
          const reconstructedGoals = [];
          existingReport.player_stats.forEach(stat => {
            // Add goals for this player
            for (let i = 0; i < (stat.goals || 0); i++) {
              reconstructedGoals.push({
                scorer: stat.player_id,
                assist: null // Can't reconstruct which assist went with which goal
              });
            }
          });
          setGoals(reconstructedGoals);
        }
      }
    } catch (error) {
      console.error("Error fetching session:", error);
      toast.error("Failed to load session details");
    } finally {
      setLoading(false);
    }
  };

  const handlePlayerStatChange = (playerId, field, value) => {
    setPlayerStats(prev => ({
      ...prev,
      [playerId]: {
        ...prev[playerId],
        [field]: value
      }
    }));
  };

  const addGoal = () => {
    setGoals([...goals, { scorer: null, assist: null }]);
  };

  const updateGoal = (index, field, value) => {
    const newGoals = [...goals];
    newGoals[index][field] = value;
    setGoals(newGoals);
  };

  const removeGoal = (index) => {
    const newGoals = goals.filter((_, i) => i !== index);
    setGoals(newGoals);
  };

  const calculatePlayerStats = () => {
    const stats = {};
    
    // Initialize all players with 0 stats
    players.forEach(player => {
      stats[player.id] = { goals: 0, assists: 0, motm: playerStats[player.id]?.motm || false };
    });

    // Count goals and assists from the goals array
    goals.forEach(goal => {
      if (goal.scorer && goal.scorer !== 'OG') {
        stats[goal.scorer] = stats[goal.scorer] || { goals: 0, assists: 0, motm: false };
        stats[goal.scorer].goals++;
      }
      if (goal.assist) {
        stats[goal.assist] = stats[goal.assist] || { goals: 0, assists: 0, motm: false };
        stats[goal.assist].assists++;
      }
    });

    return stats;
  };

  const submitReport = async () => {
    if (reportType === "match") {
      if (!result) {
        toast.error("Please select match result");
        return;
      }
      if (!ourScore || !opponentScore) {
        toast.error("Please enter both scores");
        return;
      }
      if (!opponentName) {
        toast.error("Please enter opponent name");
        return;
      }

      // Validate that total goals tracked = our score
      const ourScoreInt = parseInt(ourScore);
      if (goals.length !== ourScoreInt) {
        toast.error(`Please record all ${ourScoreInt} goal(s). Currently recorded: ${goals.length}`);
        return;
      }

      // Validate that each goal has a scorer
      const missingScorers = goals.filter(g => !g.scorer);
      if (missingScorers.length > 0) {
        toast.error(`Please select a scorer for all goals`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const calculatedStats = calculatePlayerStats();
      const playerStatsArray = Object.keys(calculatedStats).map(playerId => ({
        player_id: playerId,
        goals: calculatedStats[playerId].goals,
        assists: calculatedStats[playerId].assists,
        motm: calculatedStats[playerId].motm
      }));

      const payload = {
        type: reportType,
        result: result || null,
        our_score: ourScore ? parseInt(ourScore) : null,
        opponent_score: opponentScore ? parseInt(opponentScore) : null,
        opponent_name: opponentName || null,
        match_notes: matchNotes,
        player_stats: playerStatsArray
      };

      if (isEditMode) {
        await axios.put(
          `${API}/academy/sessions/${sessionId}/match-report`,
          payload,
          { withCredentials: true }
        );
        toast.success("Match report updated successfully!");
      } else {
        await axios.post(
          `${API}/academy/sessions/${sessionId}/match-report`,
          payload,
          { withCredentials: true }
        );
        toast.success("Match report submitted successfully!");
      }

      navigate(`/academy/sessions`);
    } catch (error) {
      console.error("Error submitting report:", error);
      toast.error(error.response?.data?.detail || "Failed to submit match report");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <Card className="p-8">
          <p className="text-slate-400">Session not found</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      <header className="bg-slate-950 border-b border-white/5 shadow-lg">
        <div className="container mx-auto px-4 py-4">
          <Link to="/academy/sessions">
            <Button variant="ghost" className="text-white hover:text-blue-200">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Sessions
            </Button>
          </Link>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <h2 className="text-3xl font-bold text-white mb-2">
          {isEditMode ? "Edit Match Report" : "Submit Match Report"}
        </h2>
        <p className="text-slate-400 mb-6">{session.team_name} - {new Date(session.session_date).toLocaleDateString()}</p>
        {isEditMode && (
          <div className="mb-4 p-3 bg-yellow-500/10 border border-white/5 rounded">
            <p className="text-sm text-yellow-400">
              ⚠️ <strong>Edit Mode:</strong> Updating this report will adjust team and player stats accordingly.
            </p>
          </div>
        )}

        <Card className="p-6 mb-6">
          <h3 className="text-xl font-bold text-white mb-4">Session Type</h3>
          <div className="flex gap-4">
            <Button
              onClick={() => setReportType("training")}
              variant={reportType === "training" ? "default" : "outline"}
              className={reportType === "training" ? "bg-blue-600" : ""}
            >
              Training Session
            </Button>
            <Button
              onClick={() => setReportType("match")}
              variant={reportType === "match" ? "default" : "outline"}
              className={reportType === "match" ? "bg-green-600" : ""}
            >
              Match/Game
            </Button>
          </div>
        </Card>

        {reportType === "match" && (
          <Card className="p-6 mb-6">
            <h3 className="text-xl font-bold text-white mb-4">Match Details</h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Result *</label>
                <div className="flex gap-3">
                  <Button
                    onClick={() => setResult("win")}
                    variant={result === "win" ? "default" : "outline"}
                    className={result === "win" ? "bg-green-600" : ""}
                  >
                    Win
                  </Button>
                  <Button
                    onClick={() => setResult("loss")}
                    variant={result === "loss" ? "default" : "outline"}
                    className={result === "loss" ? "bg-red-600" : ""}
                  >
                    Loss
                  </Button>
                  <Button
                    onClick={() => setResult("draw")}
                    variant={result === "draw" ? "default" : "outline"}
                    className={result === "draw" ? "bg-yellow-600" : ""}
                  >
                    Draw
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Our Score *</label>
                  <Input
                    type="number"
                    min="0"
                    value={ourScore}
                    onChange={(e) => setOurScore(e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Opponent Score *</label>
                  <Input
                    type="number"
                    min="0"
                    value={opponentScore}
                    onChange={(e) => setOpponentScore(e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Opponent Name *</label>
                <Input
                  value={opponentName}
                  onChange={(e) => setOpponentName(e.target.value)}
                  placeholder="e.g., Springfield U10 Boys"
                />
              </div>
            </div>
          </Card>
        )}

        <Card className="p-6 mb-6">
          <h3 className="text-xl font-bold text-white mb-4">Goals Breakdown</h3>
          <p className="text-sm text-slate-400 mb-4">
            Record each goal scored. Our Score: <strong>{ourScore || 0}</strong> | Goals Recorded: <strong>{goals.length}</strong>
            {ourScore && goals.length !== parseInt(ourScore) && (
              <span className="ml-2 text-orange-400 font-semibold">
                ⚠️ Please record all {ourScore} goal(s)
              </span>
            )}
          </p>

          {/* Goals List */}
          <div className="space-y-3 mb-4">
            {goals.map((goal, index) => (
              <div key={index} className="p-4 bg-[#0a0f1a] rounded-lg border-2 border-white/5">
                <div className="flex items-start justify-between mb-3">
                  <h4 className="font-semibold text-white">Goal #{index + 1}</h4>
                  <button
                    onClick={() => removeGoal(index)}
                    className="text-red-500 hover:text-red-400 text-sm font-medium"
                  >
                    ✕ Remove
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Scorer */}
                  <div>
                    <label className="text-sm font-semibold text-slate-300 mb-2 flex items-center">
                      <Trophy className="w-4 h-4 mr-1 text-emerald-400" />
                      Scorer *
                    </label>
                    <PlayerSearchDropdown
                      players={[...players, { id: 'OG', child_name: 'Own Goal (OG)', membership_number: 'Opponent' }]}
                      onSelect={(playerId) => updateGoal(index, 'scorer', playerId)}
                      placeholder="Select scorer..."
                      selectedValue={goal.scorer}
                    />
                  </div>

                  {/* Assist (optional, only if not OG) */}
                  <div>
                    <label className="text-sm font-semibold text-slate-300 mb-2 flex items-center">
                      <Target className="w-4 h-4 mr-1 text-blue-400" />
                      Assist (Optional)
                    </label>
                    {goal.scorer && goal.scorer !== 'OG' ? (
                      <PlayerSearchDropdown
                        players={players.filter(p => p.id !== goal.scorer)}
                        onSelect={(playerId) => updateGoal(index, 'assist', playerId)}
                        placeholder="Select assist..."
                        selectedValue={goal.assist}
                        allowClear={true}
                        onClear={() => updateGoal(index, 'assist', null)}
                      />
                    ) : (
                      <div className="text-sm text-slate-400 italic p-2 bg-slate-800/50 rounded border">
                        {goal.scorer === 'OG' ? 'No assist for own goals' : 'Select scorer first'}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Add Goal Button */}
          {(!ourScore || goals.length < parseInt(ourScore)) && (
            <Button
              onClick={addGoal}
              variant="outline"
              className="w-full border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Goal
            </Button>
          )}

          {/* Man of the Match */}
          <div className="border-t mt-6 pt-6">
            <label className="text-sm font-semibold text-white flex items-center mb-3">
              <Award className="w-5 h-5 mr-2 text-yellow-500" />
              Man of the Match
            </label>
            <PlayerSearchDropdown
              players={players}
              onSelect={(playerId) => {
                const newStats = { ...playerStats };
                Object.keys(newStats).forEach(id => {
                  newStats[id] = newStats[id] || { goals: 0, assists: 0, motm: false };
                  newStats[id].motm = id === playerId;
                });
                setPlayerStats(newStats);
              }}
              placeholder="Select MOTM..."
              selectedValue={Object.keys(playerStats).find(id => playerStats[id]?.motm)}
            />
          </div>

          {/* Stats Preview */}
          {goals.length > 0 && (
            <div className="border-t mt-6 pt-6">
              <h4 className="font-semibold text-white mb-3">Stats Summary</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {(() => {
                  const stats = calculatePlayerStats();
                  const ownGoalCount = goals.filter(g => g.scorer === 'OG').length;
                  
                  return (
                    <>
                      {Object.entries(stats)
                        .filter(([_, s]) => s.goals > 0 || s.assists > 0)
                        .map(([playerId, stat]) => {
                          const player = players.find(p => p.id === playerId);
                          return (
                            <div key={playerId} className="p-2 bg-blue-500/10 rounded border border-blue-500/30 text-sm">
                              <p className="font-semibold text-white">{player?.child_name}</p>
                              <div className="flex gap-2 text-xs text-slate-400 mt-1">
                                {stat.goals > 0 && <span>⚽ {stat.goals}G</span>}
                                {stat.assists > 0 && <span>🎯 {stat.assists}A</span>}
                              </div>
                            </div>
                          );
                        })}
                      {ownGoalCount > 0 && (
                        <div className="p-2 bg-slate-800/50 rounded border border-slate-700 text-sm">
                          <p className="font-semibold text-white">Own Goals</p>
                          <div className="text-xs text-slate-400 mt-1">⚽ {ownGoalCount}</div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </Card>

        <Card className="p-6 mb-6">
          <h3 className="text-xl font-bold text-white mb-4">Match Notes</h3>
          <textarea
            value={matchNotes}
            onChange={(e) => setMatchNotes(e.target.value)}
            placeholder="Team performance observations, tactics, areas for improvement..."
            className="w-full p-4 border border-slate-700 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[120px] resize-y"
            rows={5}
          />
        </Card>

        <Button
          onClick={submitReport}
          disabled={submitting}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg font-semibold"
        >
          {submitting ? (
            <>
              <div className="spinner mr-2"></div>
              Submitting...
            </>
          ) : (
            <>
              <Send className="w-5 h-5 mr-2" />
              {isEditMode ? "Update Match Report" : "Submit Match Report"}
            </>
          )}
        </Button>

        <p className="text-xs text-slate-400 text-center mt-4">
          Once submitted, team and player stats will be automatically updated
        </p>
      </div>
    </div>
  );
}
